import './facetListComponent.scss';
import React, {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import {Button, ListGroup, ListGroupItem} from 'react-bootstrap';
import {FacetGroupComponent} from './facetGroupComponent';
import {FacetGroup, FacetValuesSortOrder, HibuFacetErrorCode, ReduxFacetGroup, SelectionType} from './facetTypes';
import {getNormalizedHibuFacets, isClearable} from './facetUtils';
import {useTranslation} from 'react-i18next';
import {Option} from '../common/select/select';
import {SortDropdown} from '../common/sort-dropdown/sortDropdown';
import {SvgIcon} from '../icons/svgIcon';
import {Icons} from '../icons/icons';
import {useDispatch, useSelector} from 'react-redux';
import {
    clearSelectedFacets,
    expandCollapseFacetGroupAction,
    expandFacetListAction,
    facetValuesSortOrderChangeAction,
    filterFacets
} from '../search/duck/search-actions';
import {
    useSearchPageType,
    useStateFacetListExpanded,
    useStateFacets,
    useStateFacetsPending,
} from '../search/search-hooks';
import {DrawerProps, HibuDrawer} from '../common/drawer/hibuDrawer';
import {useHistory} from 'react-router-dom';
import {useStateContainerFacets} from '../settings/duck/settings-hooks';
import classNames from 'clsx';
import {HibuButton} from '../common/hibuButton/hibuButton';
import {changeRetainFilterChangedAction} from './duck/facet-filter-actions';
import {Checkbox} from '../common/checkbox/checkbox';
import {Spinner} from '../common/spinner/spinner';
import {FacetGroupHeader} from './facetGroupHeader';
import {FacetErrorMessage} from './facetErrorMessage';

type FacetListComponentProps = {
    showSidebar: boolean;
    closeDrawer: () => void;
};
const facetGroupsWithTooltip = ['metadata.folder_dirPath'];

export const FacetListComponent = React.memo(({showSidebar, closeDrawer}: FacetListComponentProps) => {
    const searchPageType = useSearchPageType();
    const facets = useStateFacets();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const containerFacets = useStateContainerFacets();
    const showFacetList = useStateFacetListExpanded();
    const areFacetsPending = useStateFacetsPending();
    const settingsRetainFilterAcrossSearches = useSelector((state) => state.facetFilter.retainFilterAcrossSearches);
    const [retainFilterAcrossSearches, setRetainFilterAcrossSearches] = useState(settingsRetainFilterAcrossSearches);

    const history = useHistory();

    const onExpand = () => {
        const newSate = !showFacetList;
        dispatch(expandFacetListAction(newSate));
    };

    const onFacetChange = useCallback(
        (facetGroup, newSelectedFacetValues) =>
            dispatch(filterFacets(searchPageType, facetGroup, newSelectedFacetValues, history, containerFacets)),
        [dispatch, searchPageType, history, containerFacets]
    );

    const onSortFacets = useCallback(
        (sortField: FacetValuesSortOrder) => {
            dispatch(facetValuesSortOrderChangeAction(sortField, searchPageType));
        },
        [dispatch, searchPageType]
    );

    const onFacetExpandCollapseChangeAction = useCallback(
        (fieldName: string) => dispatch(expandCollapseFacetGroupAction(fieldName, searchPageType)),
        [dispatch, searchPageType]
    );

    const onClearSelectedFacets = useCallback(() => {
        dispatch(clearSelectedFacets(searchPageType, history));
    }, [dispatch, searchPageType, history]);

    const hasSelectedFacets = (): boolean => {
        return !!facets.selectedFacetValuesByFieldName.allKeys.find((fieldName: string) =>
            isClearable(facets, fieldName)
        );
    };

    const sortOptions = [
        {value: FacetValuesSortOrder.COUNT, label: t('facets.sortBy.' + FacetValuesSortOrder.COUNT)},
        {value: FacetValuesSortOrder.NAME, label: t('facets.sortBy.' + FacetValuesSortOrder.NAME)},
    ];

    const renderCrossQueryFilterToggle = () => {
        return (
            <div className={'facets-retain-filter-wrapper'}>
                <div className={'facets-retain-filter-item'}>
                    <Checkbox value={retainFilterAcrossSearches} mayEdit={true}
                              labelKey={t('settings.retainFilterAcrossSearches')}
                              onChange={(e) => setRetainFilterAcrossSearches(e)}/>
                </div>
            </div>
        );
    };

    const renderHeader = () => {
        return (
            <div id="facets-header">
                {hasSelectedFacets() && (
                    <Button className="facet-title clear-filter-button" onClick={onClearSelectedFacets}>
                        {t('facets.clearAll')}
                    </Button>
                )}
                {!hasSelectedFacets() && (
                    <span className="facet-title">{t('facets.title')}</span>
                )}
                {
                    // TODO: add fallback for facetSortField
                    facets.facetValuesSortOrder && (
                        <SortDropdown
                            id="facet-sort"
                            value={sortOptions.find(
                                (option) => FacetValuesSortOrder[option.value.toUpperCase()] === facets.facetValuesSortOrder
                            )}
                            onChange={(selectedOption: Option) => onSortFacets && onSortFacets(selectedOption.value)}
                            options={sortOptions}
                        />
                    )
                }
            </div>
        );
    };

    useEffect(() => {
        dispatch(changeRetainFilterChangedAction(retainFilterAcrossSearches));
    }, [retainFilterAcrossSearches, setRetainFilterAcrossSearches, dispatch]);


    const facetListRef = useRef(null);
    const facetsWithValuesOrErrors = facets.facetGroups.allKeys.filter(
        (id) =>
            facets.facetGroups.byKey[id]?.selectionType !== SelectionType.HIDDEN &&
            (!!facets.facetGroups.byKey[id]?.hibuFacetValues.length || facets.facetGroups.byKey[id]?.errorCode)
    );

    function getFacetsWithErrorCode(): Map<string, HibuFacetErrorCode> {
        const facetsGroupsByKey = facets.facetGroups.byKey as ReduxFacetGroup;
        const result = new Map<string, HibuFacetErrorCode>();

        Object.keys(facetsGroupsByKey).forEach(key => {
            const errorCode = facetsGroupsByKey[key].errorCode;
            if (errorCode) {
                result.set(key, errorCode);
            }
        });

        return result;
    }

    const facetsKeysWithErrorCode: Map<string, HibuFacetErrorCode> = getFacetsWithErrorCode();


    const mobileHeaderCallback = ({onCloseRequest}: DrawerProps) => (
        <Fragment>
            <h2 className="details-header-title">{t('facets.title')}</h2>
            <SvgIcon
                className="details-header-close"
                icon={Icons.X}
                onClick={onCloseRequest}
                alt={t('general.close')}
            />
        </Fragment>
    );

    function renderSpinner() {
        return <div className={'hibu-content-loader'}>
            <Spinner/>
        </div>;
    }

    function renderFacetWithError(fieldName: string) {
        const errorCode = facetsKeysWithErrorCode.get(fieldName);
        if (errorCode === undefined) {
            return <></>;
        }
        return (<ListGroupItem key={fieldName} className={'expanded'}>
            <FacetGroupHeader onClick={() => onFacetExpandCollapseChangeAction(fieldName)}
                              isExpanded={facets.expandedFacetGroups[fieldName]} filter={''} fieldName={fieldName}
                              isSearchable={false}/>
            {facets.expandedFacetGroups[fieldName] && errorCode &&
                <FacetErrorMessage errorCode={errorCode}/>}
        </ListGroupItem>);
    }

    function renderFacetComponent(fieldName: string) {
        return <FacetGroupComponent
            key={fieldName}
            facetListRef={facetListRef}
            normalizedValues={getNormalizedHibuFacets(facets, fieldName)}
            selectionType={facets.facetGroups.byKey[fieldName]?.selectionType}
            group={facets.facetGroups.byKey[fieldName] as FacetGroup}
            onChange={onFacetChange}
            onExpandCollapse={onFacetExpandCollapseChangeAction}
            isExpanded={facets.expandedFacetGroups[fieldName]}
            withTooltip={facetGroupsWithTooltip.includes(fieldName)}
            selectedFacetValues={facets.selectedFacetValuesByFieldName.byKey[fieldName] as string[]}
        />;
    }

    const contentCallback = () => {
        let facetContent = <></>;
        if (facetsWithValuesOrErrors.length === 0 && areFacetsPending) {
            facetContent = renderSpinner();
        } else if (facetsWithValuesOrErrors.length > 0) {
            facetContent =                 <>
                <div id={'details-collapsed'} onClick={onExpand}
                     className={classNames(showFacetList ? '' : 'hideContent')}>
                    <SvgIcon
                        icon={Icons.FILTER}
                        className={'facet-list-collapsed-filter'}
                    />
                    <SvgIcon
                        icon={Icons.ARROWS}
                        className={'facet-list-collapsed-arrows'}
                    />
                </div>
                <div id="facets-inner" className={classNames(showFacetList ? '' : 'hideContent')}>
                    {renderHeader()}
                    {renderCrossQueryFilterToggle()}
                    {<div id="facets-list" ref={facetListRef}>
                        <ListGroup>
                            {facetsWithValuesOrErrors.map((fieldName: string) => (
                                facetsKeysWithErrorCode.has(fieldName) ? renderFacetWithError(fieldName) : renderFacetComponent(fieldName)
                            ))}
                        </ListGroup>
                    </div>}
                    <HibuButton
                        size={'sm'}
                        onClick={onExpand}
                        icon={Icons.ARROWS}
                        tooltipKey={'search.details.hideDetails'}
                        visible={!!facetsWithValuesOrErrors.length}
                        className={`facet-list-close ${showFacetList ? '' : 'hideContent'}`}
                        placement={'right'}
                    />
                </div>
            </>;
        }
        return (<div id={'facets'}
                     className={areFacetsPending && facetsWithValuesOrErrors.length > 0 ? 'fetch-pending' : ''}>
            {facetContent}
        </div>);
    };

    return (
        <HibuDrawer
            id={'hibu_facet_column'}
            className={classNames(showFacetList ? '' : 'hideContent')}
            show={showSidebar}
            onCloseRequest={closeDrawer}
            headerCbMobile={mobileHeaderCallback}
            contentCb={contentCallback}
        />
    );
});
